import { getAuthCookie } from '@/utilities/cookies';
import { getServices } from '@simplywallst/services';
import { z } from 'zod';

export class HumanReadableError extends Error {}

export const SUPPORTED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
];

export const TOTAL_IMAGE_SIZE_LIMIT = 10 * 1024 * 1024; // 10MB
export const RTE_FILE_UPLOAD_EVENT_NAME = 'rte-file-upload-abort';

const services = getServices();

type UploadFileOptions = {
  onProgress?: (progressPercentage: number) => void;
  onUploadInitiated?: ({ abort }: { abort: () => void }) => void;
};

export const uploadFile = async (
  file: File,
  { onProgress, onUploadInitiated }: UploadFileOptions
): Promise<Response | null> => {
  const formData = new FormData();
  formData.append('files', file);
  const xhr = new XMLHttpRequest();
  const auth = await getAuthCookie();
  const accessToken = auth?.accessToken;

  if (!accessToken) return Promise.reject(new Error('Unauthenticated!'));

  return new Promise((resolve, reject) => {
    xhr.responseType = 'blob';
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.UNSENT) return resolve(null);
      if (xhr.readyState !== XMLHttpRequest.DONE) {
        return;
      }

      if (xhr.status === 0) {
        return resolve(null);
      }

      const response = new Response(xhr.response, {
        status: xhr.status,
        statusText: xhr.statusText,
      });

      resolve(response);
    };

    xhr.addEventListener('error', () => {
      reject(new TypeError('Failed to upload file'));
    });

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        onProgress((e.loaded / e.total) * 100);
      };
    }

    if (!services.SWS_IMAGE_SERVICE)
      throw new Error('Missing env variable SWS_IMAGE_SERVICE.');

    xhr.open('POST', new URL('/upload', services.SWS_IMAGE_SERVICE).href, true);
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    xhr.send(formData);
    onUploadInitiated?.({
      abort: () => {
        xhr.abort();
      },
    });
  });
};

export const selectFile = () => {
  return new Promise<File | null>((resolve) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', SUPPORTED_MIME_TYPES.join(', '));
    input.addEventListener('cancel', () => {
      resolve(null);
    });
    input.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];

      resolve(file || null);
    });
    input.click();
  });
};

export const ImageUploadResponseSchema = z.array(z.string());

export const validateFile = (file: File) => {
  if (file.size > TOTAL_IMAGE_SIZE_LIMIT)
    throw new HumanReadableError(
      'File size exceeds the allowed limit. Maximum 10MB per image.'
    );
  if (!SUPPORTED_MIME_TYPES.includes(file.type))
    throw new HumanReadableError(
      'Unsupported image format. Supported formats are PNG, JPEG, JPG, GIF.'
    );
};
